import { useCallback, useEffect, useState } from 'react';
import { getDocs } from 'firebase/firestore';
import styled from 'styled-components';
import { Status } from 'shared/enums';
import {
  ARTIST_PUZZLE_COUNT_DROPDOWN_OPTIONS,
  ARTIST_PUZZLE_COUNT_VALUES,
  ARTIST_STATUS_DROPDOWN_ITEMS,
  ARTIST_SUSPENDED_DROPDOWN_OPTIONS,
} from 'app-constants';

import { FilterItem, getQuery } from '../../../../services';
import { Select, Splash } from '../../../../shared/Components';
import { TableComponent } from '../../../../shared/Components/Table';
import { Box, FlexContainer } from '../../../../shared/Typhography';
import { strings } from '../../../../strings';

import { useArtistTableHeaders } from './useArtistTableHeaders';

const TableWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 44vh;
  margin-top: 1rem;
`;

const StyledTable = styled(TableComponent)`
  min-height: 44vh;

  & > div,
  & > div > div {
    min-height: 44vh;
  }
`;

interface IArtistRowData {
  id: string;
  profileImage: string;
  name: string;
  username: string;
  imagesUploaded: number;
  followers: number;
  revenueShare: string;
  status: Status;
  createdAt: number | null;
}

type PageFilters = {
  status: Status | null;
  puzzlesCount: keyof typeof ARTIST_PUZZLE_COUNT_VALUES;
  suspended: boolean | string;
};

export default function ArtistsScreen() {
  const [isLoading, setIsLoading] = useState<boolean>();

  const [artists, setArtists] = useState<Array<IArtistRowData>>([]);
  const [numberOfArtists, setNumberOfArtists] = useState<number>();
  const [filters, setFilters] = useState<PageFilters>({
    status: null,
    puzzlesCount: ARTIST_PUZZLE_COUNT_VALUES.withPuzzles,
    suspended: 'all',
  });

  const headers = useArtistTableHeaders(artists);

  const fetchArtists = useCallback(async () => {
    setIsLoading(true);

    const statementFilters: Array<FilterItem> = [];

    if (filters.status) {
      statementFilters.push({ attribute: 'status', operator: '==', value: filters.status });
    }

    if (filters.suspended && filters.suspended !== 'all') {
      statementFilters.push({
        attribute: 'flags',
        operator: 'array-contains',
        value: 'suspended',
      });
    }

    if (filters.puzzlesCount && filters.puzzlesCount !== 'all') {
      if (filters.puzzlesCount === 'withPuzzles') {
        statementFilters.push({ attribute: 'Puzzles', operator: '>', value: 0 });
      } else if (filters.puzzlesCount === 'withoutPuzzles') {
        statementFilters.push({ attribute: 'Puzzles', operator: '<=', value: 0 }); // "<=" so we can detect if there is problem with puzzle count.
      }
    }

    const userDocs = await getDocs(
      getQuery(
        'usersWeb',
        [{ attribute: 'Role', value: 'artist', operator: '==' }, ...statementFilters],
        [],
      ),
    );

    // map artist data and sort data
    const artistsData = userDocs?.docs
      ?.map((item) => {
        const id = item.id;
        const itm = item.data();

        return {
          id: id,
          profileImage: itm.profileImageUrl,
          name: itm.Name,
          username: itm.username,
          imagesUploaded: itm.Puzzles,
          followers: itm.Followers,
          revenueShare: itm?.revenueShare
            ? `${Math.round(itm?.revenueShare * 100)} %`
            : strings.notSet,
          status: itm.status,
          createdAt: itm?.createdAt ? new Date(itm.createdAt.toDate()).getTime() : null,
          requiresInitialApproval: Boolean(itm?.requiresInitialApproval),
          //Todo: Use enums from DashboardRoutePath. We have some imports problem atm. Need to resolve that first.
          __onRowClick__: () => window.open(`/view-artist/${id}`),
        };
      })
      .filter((data) => data.status);

    setArtists(artistsData);
    setNumberOfArtists(artistsData.length);
    setIsLoading(false);
  }, [filters]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  return (
    <Box margin="150px auto" style={{ minHeight: '66vh' }}>
      <FlexContainer gap="1rem" flexWrap="wrap" margin="0 0 20px 0">
        <Select
          id="artist-status-filter"
          label="Status by"
          onChange={(e) => setFilters({ ...filters, status: e?.value as Status })}
          options={ARTIST_STATUS_DROPDOWN_ITEMS}
          value={ARTIST_STATUS_DROPDOWN_ITEMS.find((itm) => itm?.value == filters.status)}
          style={{ width: '200px' }}
          isClearable={true}
        />

        <Select
          id="artist-status-filter"
          label="Filter by puzzle count"
          onChange={(e) => {
            setFilters({ ...filters, puzzlesCount: e?.value as PageFilters['puzzlesCount'] });
          }}
          options={ARTIST_PUZZLE_COUNT_DROPDOWN_OPTIONS}
          value={ARTIST_PUZZLE_COUNT_DROPDOWN_OPTIONS.find((itm) => itm?.value === filters.status)}
          style={{ width: '200px' }}
          defaultValue={ARTIST_PUZZLE_COUNT_DROPDOWN_OPTIONS.find(
            (option) => option.value === filters.puzzlesCount,
          )}
        />

        <Select
          id="artist-suspended-filter"
          label="Suspended"
          onChange={(e) => {
            setFilters({ ...filters, suspended: e?.value as boolean });
          }}
          options={ARTIST_SUSPENDED_DROPDOWN_OPTIONS}
          value={ARTIST_SUSPENDED_DROPDOWN_OPTIONS.find((itm) => itm?.value === filters.suspended)}
          style={{ width: '200px' }}
          defaultValue={ARTIST_SUSPENDED_DROPDOWN_OPTIONS.find(
            (option) => option.value === filters.suspended,
          )}
        />
      </FlexContainer>
      <FlexContainer style={{ position: 'relative' }} gap="1rem" flexWrap="wrap">
        <b>Number of artists:</b> {isLoading ? '-' : numberOfArtists}
      </FlexContainer>
      <TableWrapper>
        {isLoading ? (
          <Splash />
        ) : (
          <StyledTable
            columns={headers}
            data={artists}
            initialState={{
              sortBy: [
                {
                  id: 'createdAt',
                  desc: true,
                },
              ],
            }}
          />
        )}
      </TableWrapper>
    </Box>
  );
}
